import { Component, inject, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MaterialModule } from '../../../shared/material/material.module';
import { AccessPlan } from '../../../core/generated';

@Component({
  selector: 'app-admin-plan-dialog',
  standalone: true,
  imports: [MaterialModule],
  template: `
    <div class="modal-backdrop">
      <div class="modal-container">
        <div class="modal">
          <div class="modal-header">
            <h3 class="modal-title">{{ data.plan ? 'Editar Plano' : 'Novo Plano' }}</h3>
          </div>

          <mat-dialog-content class="modal-body">
            <form [formGroup]="planForm">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>CPF/CNPJ</mat-label>
                <input matInput formControlName="cpfcnpj" placeholder="Digite apenas números">
                <mat-error *ngIf="planForm.get('cpfcnpj')?.hasError('required')">Campo obrigatório</mat-error>
                <mat-error *ngIf="planForm.get('cpfcnpj')?.hasError('minLength')">CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos</mat-error>
                <mat-hint>CPF: 11 dígitos, CNPJ: 14 dígitos</mat-hint>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Tipo de Pacote</mat-label>
                <input matInput formControlName="packageType" placeholder="Ex: PREMIUM">
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>CNPJ do Pagador</mat-label>
                <input matInput formControlName="payerCnpj" placeholder="Digite apenas números">
                <mat-hint>Opcional - 14 dígitos</mat-hint>
              </mat-form-field>

              <mat-checkbox formControlName="autoRenew">Renovação Automática</mat-checkbox>
              <mat-checkbox formControlName="active">Plano Ativo</mat-checkbox>
            </form>
          </mat-dialog-content>

          <mat-dialog-actions class="modal-footer">
            <button mat-button class="btn btn-ghost" (click)="onCancel()">
              <mat-icon>close</mat-icon> Cancelar
            </button>
            <button mat-flat-button color="primary" class="btn btn-primary" (click)="onSave()" [disabled]="planForm.invalid">
              <mat-icon>save</mat-icon> Salvar
            </button>
          </mat-dialog-actions>
        </div>
      </div>
    </div>
  `,
  styles: [`
    mat-form-field { 
      width: 100%; 
      margin-bottom: var(--spacing-sm); 
    }
    mat-checkbox {
      display: block;
      margin-bottom: var(--spacing-sm);
    }
  `]
})
export class AdminPlanDialogComponent {
  fb = inject(FormBuilder);
  dialogRef = inject(MatDialogRef<AdminPlanDialogComponent>);

  planForm = this.fb.group({
    id: [null as number | null],
    cpfcnpj: ['', [Validators.required, Validators.minLength(11)]],
    packageType: [''],
    payerCnpj: [''],
    autoRenew: [false],
    active: [true],
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: { plan?: AccessPlan }) {
    if (this.data.plan) {
      this.planForm.patchValue({
        id: this.data.plan.id ?? null,
        cpfcnpj: this.data.plan.cpfcnpj ?? '',
        packageType: this.data.plan.packageType ?? '',
        payerCnpj: this.data.plan.payerCnpj ?? '',
        autoRenew: this.data.plan.autoRenew ?? false,
        active: this.data.plan.active ?? true,
      });
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.planForm.valid) {
      const cleanCpfCnpj = this.planForm.value.cpfcnpj!.replace(/\D/g, '');

      if (cleanCpfCnpj.length !== 11 && cleanCpfCnpj.length !== 14) {
        return;
      }

      const plan: AccessPlan = {
        id: this.planForm.value.id ?? undefined,
        cpfcnpj: cleanCpfCnpj,
        packageType: this.planForm.value.packageType as AccessPlan.PackageTypeEnum || undefined,
        payerCnpj: this.planForm.value.payerCnpj ? this.planForm.value.payerCnpj.replace(/\D/g, '') : undefined,
        autoRenew: this.planForm.value.autoRenew ?? false,
        active: this.planForm.value.active ?? true,
      };
      this.dialogRef.close(plan);
    }
  }
}
